import { ReactElement } from "react";
import { ISample, ISteel } from "../../../../types";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { formatNumber, formatTimeWithSeconds } from "../../../../system/utils";
import CircularProgress from "@mui/material/CircularProgress";
import { Alert } from "@mui/material";
import { useFetch } from "../../../../system/useFetch";
import TableContainer  from "../../../../components/tableContainer";

interface IGeneralPanelProps {
  steelCode: string;
  zoneCode: string;
  samples: ISample[];
}

interface ICompositionRowProps {
  columnName: string;
  steel: ISteel;
  className: any;
}
const CompositionRow = ({ columnName, steel: steelItem, className }: ICompositionRowProps) => {
  return (
      <TableRow tabIndex={-1} key={`composition-row-${columnName}`} >
        <TableCell key={`${columnName}-$datetime`} align="left" className={className}>
          {columnName.toUpperCase()}
        </TableCell>
        <TableCell style={{ minWidth: 80, width: 80 }} className={className}/>
        <TableCell style={{ minWidth: 80, width: 80 }} className={className}/>
        {steelItem.composition.map((element, index) => {
          const value = (element as any)[columnName];

          return (
              <TableCell key={`${columnName}-${element.code}-${index}`} align="right" className={className}>
                {formatNumber(value, 4)}
              </TableCell>
          );
        })}
      </TableRow>
  );
};

const SamplePanel = ({ steelCode, zoneCode, samples }: IGeneralPanelProps): ReactElement => {
  const { loading, error, data } = useFetch<ISteel>({ module: "metallurgical", query: "getSteelComposition", payload: { steelCode, zoneCode } });
  const canShowData = !loading && !error;

  const steel: ISteel | null = canShowData ? data : null;

  console.log("SamplePanel", { steelCode, zoneCode, samples, steel, loading, error, data, canShowData });

  return(
    <TableContainer>
      {loading && <div className="text-center"><CircularProgress/></div>}
      {error && <Alert severity="warning">{error.message}</Alert>}
      {steel !== null &&
        <Table stickyHeader={true} size={"small"}>
          <TableHead >
            <TableRow >
              <TableCell
                className="table-header"
                align="left"
                style={{ minWidth: 100, width: 100 }}>
                Datetime
              </TableCell>
              <TableCell
                className="table-header"
                align="left"
                style={{ minWidth: 80, width: 80 }}>
                Machine
              </TableCell>
              <TableCell
                className="table-header"
                align="left"
                style={{ minWidth: 80, width: 80 }}>
                Type
              </TableCell>

              {steel.composition.map((element, index) =>
                <TableCell
                  className="table-header"
                  key={`element-${index}`}
                  align="right"
                  style={{ minWidth: 50, width: 50 }}>
                  {element.code}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <CompositionRow columnName="maximum" steel={steel} className="warning-value"/>
            <CompositionRow columnName="optimal" steel={steel} className="ok-value"/>
            <CompositionRow columnName="minimum" steel={steel} className="warning-value"/>

            { samples.filter(s => !s.sampleType.code.startsWith("LT")).map((sample, index) => {
              const rowKey = `sample-${sample.sampleHistoryID}-${index}`;

              return(
                <TableRow key={rowKey} tabIndex={-1} >
                  <TableCell align="left">
                    {sample.sampleDatetime ? formatTimeWithSeconds(sample.sampleDatetime) : "---"}
                  </TableCell>
                  <TableCell align="left">
                    {sample.sampleType.machine}
                  </TableCell>
                  <TableCell align="left">
                    {sample.sampleType.code}
                  </TableCell>
                  {steel && steel.composition.map((element, elIndex) => {

                    const sampleElementKey= `${rowKey}-${elIndex}`;
                    const item = sample.items.find(x => x.name.toUpperCase() === element.code.toUpperCase());
                    let itemClass;

                    if (item) {
                      if (item.value === element.minimum) {
                        itemClass = "ok-value";
                      } else if (item.value === element.maximum) {
                        itemClass = "warning-value";
                      } else if (item.value > element.maximum) {
                        itemClass = "error-value";
                      }
                    }

                    return(
                      <TableCell
                        key={sampleElementKey}
                        className={itemClass}
                        align="right">
                        {item ? formatNumber(item.value, 4) : "---"}
                      </TableCell>);
                  })}
                </TableRow>
              );
            })
            }
          </TableBody>
        </Table>
      }
    </TableContainer>
  );
};

export default SamplePanel;
